import { E_CodeTypes, type T_CertificatesListRes } from '@/types/responses'

const PENDING_PURCHASE_LS_PREFIX = 'ERA_PENDING_PURCHASE_CERTIFICATES'
export const PENDING_PURCHASE_UPDATED_EVENT = 'era:pending-purchase-updated'

export type T_PendingPurchaseItem = {
  tempId: number
  nominalId: number
  createdAt: number
  documentId?: number
}

export const getPendingPurchaseQueueKey = (userId?: number | null) => (
  `${PENDING_PURCHASE_LS_PREFIX}_${userId || 0}`
)

const normalizePendingPurchaseQueue = (raw: unknown): T_PendingPurchaseItem[] => {
  if (!Array.isArray(raw)) return []

  return raw
    .map((item: any) => {
      const tempId = Number(item?.tempId)
      const nominalId = Number(item?.nominalId)
      const createdAt = Number(item?.createdAt)
      const documentId = Number(item?.documentId)

      if (!(tempId < 0)) return null
      if (!(nominalId > 0)) return null

      return {
        tempId,
        nominalId,
        createdAt: Number.isFinite(createdAt) && createdAt > 0 ? createdAt : Date.now(),
        documentId: Number.isFinite(documentId) && documentId > 0 ? documentId : undefined,
      } satisfies T_PendingPurchaseItem
    })
    .filter(Boolean) as T_PendingPurchaseItem[]
}

export const readPendingPurchaseQueue = (userId?: number | null): T_PendingPurchaseItem[] => {
  try {
    const raw = localStorage.getItem(getPendingPurchaseQueueKey(userId))
    if (!raw) return []
    return normalizePendingPurchaseQueue(JSON.parse(raw))
  } catch {
    return []
  }
}

export const writePendingPurchaseQueue = (
  userId: number | null | undefined,
  items: T_PendingPurchaseItem[],
) => {
  try {
    const key = getPendingPurchaseQueueKey(userId)
    const normalized = normalizePendingPurchaseQueue(items)
    const nextRaw = JSON.stringify(normalized)
    const prevRaw = localStorage.getItem(key)
    if (prevRaw === nextRaw) return

    localStorage.setItem(key, nextRaw)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(PENDING_PURCHASE_UPDATED_EVENT))
    }
  } catch {
    // ignore localStorage failures
  }
}

export const upsertPendingPurchaseQueueItem = (
  userId: number | null | undefined,
  nextItem: T_PendingPurchaseItem,
) => {
  const prev = readPendingPurchaseQueue(userId)
  const next = [ nextItem, ...prev.filter((p) => p.tempId !== nextItem.tempId) ]
  writePendingPurchaseQueue(userId, next)
}

export const removePendingPurchaseQueueItem = (
  userId: number | null | undefined,
  tempId: number,
) => {
  const prev = readPendingPurchaseQueue(userId)
  const next = prev.filter((p) => p.tempId !== tempId)
  writePendingPurchaseQueue(userId, next)
}

export const toOptimisticPurchasedCertificate = (
  item: T_PendingPurchaseItem,
): T_CertificatesListRes[number] => ({
  id: item.tempId,
  code: '',
  goods: item.nominalId,
  time: item.createdAt,
  type: E_CodeTypes.QRCode,
})
